<template>
  <Pill
    v-if="!loading && trips.length === 0"
    color="lightest-gray"
  >
    <slot name="noneFoundMessage" />
  </Pill>
  <InfiniteScroll
    v-else
    @fetch="fetch"
  >
    <template #content>
      <slot
        name="prependTable"
        v-bind="{ trips }"
      />
      <TripTable
        ref="tripTable"
        :trips="trips"
        :loading="loading"
        :row-links="rowLinks"
      />
      <slot
        name="appendTable"
        v-bind="{ trips }"
      />
    </template>
    <template #loading>
      <Loading v-if="trips.length > 0 && !loading && calendar?.next" />
    </template>
  </InfiniteScroll>
</template>

<script setup>
const localeStore = useLocaleStore()
const { getTemporaryFallbackLocale: safeLocale } = storeToRefs(localeStore)

const { endpoint, perPage, firstPage, rowLinks } = defineProps({
  endpoint: {
    type: String,
    default: '',
  },

  perPage: {
    type: Number,
    default: 20,
  },

  firstPage: {
    type: Number,
    default: 0,
  },

  rowLinks: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update'])

const loading = ref(true)
const calendar = ref({
  departures: [],
  prev: null,
  next: null,
})

const trips = computed(() => calendar.value.departures)

const lastRequestUrl = ref(null)

const fetch = async (requestUrl = null) => {
  const url = requestUrl || calendar.value?.next || null

  if (!url || lastRequestUrl.value === url) {
    return
  }

  const { data: result } = await apiFetch(url)

  calendar.value = {
    ...result,
    departures: [
      ...(calendar.value?.departures || []),
      ...(result?.departures || []),
    ],
  }
  emit('update', result)
  lastRequestUrl.value = url
}

useAsyncData('fetchData', async () => {
  const hasUrlParams = endpoint.includes('?')
  const glue = hasUrlParams ? '' : '?'
  let initialRequest = `/${safeLocale.value}/${endpoint}${glue}`

  // Backend doesn't cache if any of these are defined
  if (perPage !== 20) {
    initialRequest += `&p=${perPage}`
  }
  if (firstPage !== 0) {
    initialRequest += `&n=${firstPage}`
  }

  try {
    await fetch(initialRequest)
  } catch {
    // do nothing
  }

  loading.value = false
})
</script>
